import { useState } from 'react';

import { RowSelectionState } from '@tanstack/react-table';
import { createQueryFilters } from 'context/DataContext';
import { useFeatureFlagVersion } from 'context/FeatureFlagContext';
import { parseRowId } from 'features/drilldown/LoanDatatable/LoanTable';
import { GET_COMPANY_JOBS } from 'query/jobs';
import { SelectionStatus } from 'ui-kit';

import { useMutation } from '@apollo/client';

import {
  AssetClass,
  Filter,
  FilterableField,
  FilterOperator,
  OriginationType,
} from '__generated__/globalTypes';
// Legacy mutation (synchronous only)
import { CreateListing, CreateListingVariables } from 'mutation/__generated__/createListing';
import { CREATE_LISTING } from 'mutation/createListing';

// New mutation (supports sync/async hybrid)
import {
  CreatePool as CreatePoolMutation,
  CreatePoolVariables,
} from 'mutation/__generated__/createPool';
import { CREATE_POOL } from 'mutation/createPool';

import { FormValues, MAX_POOL_NAME_LENGTH } from '../CreatePoolForm';

type CreatePool_createPool_UserCompanyListing = Extract<
  CreatePoolMutation['createPool'],
  { __typename: 'UserCompanyListing' }
>;
type CreatePool_createPool_PoolCreationJob = Extract<
  CreatePoolMutation['createPool'],
  { __typename: 'PoolCreationJob' }
>;

export interface LastCreatedPool {
  serverId: string;
  generatedName: string;
  friendlyName: string;
}

export interface PendingPoolJob {
  jobId: string;
  poolName: string;
  friendlyName: string;
}

/**
 * Type guard to check if the result is a synchronously created pool
 */
function isUserCompanyListing(
  result: CreatePool_createPool_UserCompanyListing | CreatePool_createPool_PoolCreationJob,
): result is CreatePool_createPool_UserCompanyListing {
  return result.__typename === 'UserCompanyListing';
}

/**
 * Type guard to check if the result is an async job
 */
function isPoolCreationJob(
  result: CreatePool_createPool_UserCompanyListing | CreatePool_createPool_PoolCreationJob,
): result is CreatePool_createPool_PoolCreationJob {
  return result.__typename === 'PoolCreationJob';
}

export const useCreatePool = () => {
  // Check feature flag to determine which codepath to use
  const asyncPoolCreationVersion = useFeatureFlagVersion('asyncPoolCreation');
  const isAsyncEnabled = asyncPoolCreationVersion === 'enabled';

  // Legacy mutation (always synchronous)
  const [createListingMutation, { loading: createListingLoading }] = useMutation<
    CreateListing,
    CreateListingVariables
  >(CREATE_LISTING);

  // New mutation (supports sync/async hybrid based on pool size)
  const [createPoolMutation, { loading: createPoolMutationLoading }] = useMutation<
    CreatePoolMutation,
    CreatePoolVariables
  >(CREATE_POOL);

  const [lastCreatedPool, setLastCreatedPool] = useState<LastCreatedPool | null>(null);
  const [pendingPoolJob, setPendingPoolJob] = useState<PendingPoolJob | null>(null);

  // Combined loading state from whichever mutation is active
  const createPoolLoading = createListingLoading || createPoolMutationLoading;

  const createPool = async (
    formValues: FormValues,
    gqlAssetClass: AssetClass,
    queryFilters: Filter[],
    selectedLoans: RowSelectionState,
    selectionStatus: SelectionStatus,
    /**
     * When provided, bypasses the selection-derived filter construction
     * entirely and uses these filters as-is. Used by tape-driven entry
     * points (PoolFromTapeButton, LatestUploadBanner) that scope a
     * pool to a specific last_loan_tape_id without going through the
     * loan-table selection flow.
     */
    overrideFilters?: Filter[],
  ) => {
    const trimmedPoolName = formValues.poolName.trim();

    if (!trimmedPoolName) {
      throw new Error('Pool name is required');
    }

    if (trimmedPoolName.length > MAX_POOL_NAME_LENGTH) {
      throw new Error(`Pool name must be ${MAX_POOL_NAME_LENGTH} characters or fewer`);
    }

    let filters: Filter[];

    if (overrideFilters !== undefined) {
      filters = overrideFilters;
    } else if (selectionStatus === SelectionStatus.AllSelected) {
      // If all loans are selected, use the current query filters (v1 behavior: no unlisted filters)
      filters = createQueryFilters(gqlAssetClass, queryFilters);
    } else {
      // If not all loans are selected, create the pool with the selected loans
      const accountIdList = Object.keys(selectedLoans).map((key) => parseRowId(key).accountId);

      const filter: Filter = {
        field_name: FilterableField.account_id,
        operator: FilterOperator.IS,
        operandList: accountIdList,
      };

      filters = [filter];
    }

    // Use legacy codepath if async pool creation is not enabled
    if (!isAsyncEnabled) {
      const res = await createListingMutation({
        variables: {
          name: trimmedPoolName,
          assetClass: gqlAssetClass,
          filters: filters,
          originationType: mapFormValueToBackendOriginationType(formValues.originationType),
        },
      });

      const poolGeneratedName = res.data?.createListing?.name;
      const poolServerId = res.data?.createListing?.id;

      if (!poolServerId) {
        throw new Error('Failed to create pool - no response from server');
      }

      setLastCreatedPool({
        serverId: poolServerId,
        generatedName: poolGeneratedName || '',
        friendlyName: trimmedPoolName,
      });
      setPendingPoolJob(null);

      // Legacy path always returns sync result
      return { type: 'sync' as const, pool: { id: poolServerId, name: poolGeneratedName } };
    }

    // New async-enabled codepath
    const res = await createPoolMutation({
      variables: {
        name: trimmedPoolName,
        assetClass: gqlAssetClass,
        filters: filters,
        originationType: mapFormValueToBackendOriginationType(formValues.originationType),
      },
      refetchQueries: [GET_COMPANY_JOBS],
    });

    const result = res.data?.createPool;

    if (!result) {
      throw new Error('Failed to create pool - no response from server');
    }

    if (isUserCompanyListing(result)) {
      // Pool was created synchronously
      setLastCreatedPool({
        serverId: result.id,
        generatedName: result.name || '',
        friendlyName: trimmedPoolName,
      });
      setPendingPoolJob(null);
      return { type: 'sync' as const, pool: result };
    }

    if (isPoolCreationJob(result)) {
      // Pool creation started as async job
      setPendingPoolJob({
        jobId: result.job_id,
        poolName: result.pool_name || trimmedPoolName,
        friendlyName: trimmedPoolName,
      });
      setLastCreatedPool(null);
      return { type: 'async' as const, job: result };
    }

    throw new Error('Unexpected response type from createPool');
  };

  return { createPool, lastCreatedPool, pendingPoolJob, createPoolLoading };
};

const mapFormValueToBackendOriginationType = (formValue: string): OriginationType => {
  switch (formValue) {
    case 'direct':
      return OriginationType.DIRECT;
    case 'indirect':
      return OriginationType.INDIRECT;
    case 'indirect_fintech':
      return OriginationType.INDIRECT_FINTECH;
    case 'mixed':
      return OriginationType.MIXED;
    case 'n_a':
      return OriginationType.N_A;
  }
  return OriginationType.N_A;
};
